'use client';

import * as React from 'react';
import { X } from '@phosphor-icons/react';
import { MinusIcon, PlusIcon } from '@/components/icons';
import { cn } from '@/lib/utils';

export interface QuantitySelectorProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  size?: 'sm' | 'default' | 'lg';
  disabled?: boolean;
  showRemove?: boolean; // Показывать X вместо - когда value = 1
  className?: string;
}

// Счётчик количества — ступень pill по контракту («круглое и капсульное:
// аватар, чип, счётчик, бейдж»). Ширина и высота не тронуты: меняется только
// скругление, поэтому попадание по кнопкам «плюс» и «минус» прежнее.
const sizes = {
  sm: {
    container: 'h-8 rounded-pill',
    button: 'w-8 h-8',
    icon: 'h-3.5 w-3.5',
    text: 'text-xs min-w-[24px]',
  },
  default: {
    container: 'h-10 rounded-pill',
    button: 'w-10 h-10',
    icon: 'h-4 w-4',
    text: 'text-sm min-w-[32px]',
  },
  lg: {
    container: 'h-12 rounded-pill',
    button: 'w-12 h-12',
    icon: 'h-5 w-5',
    text: 'text-base min-w-[40px]',
  },
};

const QuantitySelector = React.memo(
  React.forwardRef<HTMLDivElement, QuantitySelectorProps>(
    (
      {
        value,
        onChange,
        min = 0,
        max = 99,
        size = 'default',
        disabled = false,
        showRemove = true,
        className,
      },
      ref
    ) => {
      const handleDecrement = React.useCallback(() => {
        if (value > min) {
          onChange(value - 1);
        }
      }, [value, min, onChange]);

      const handleIncrement = React.useCallback(() => {
        if (value < max) {
          onChange(value + 1);
        }
      }, [value, max, onChange]);

      const currentSize = sizes[size];
      const isAtMin = value <= min;
      const shouldShowRemove = showRemove && value === 1;

      return (
        <div
          ref={ref}
          data-quantity-selector
          className={cn(
            'inline-flex items-center',
            'bg-[var(--primary-subtle)]',
            'overflow-hidden',
            currentSize.container,
            disabled && 'opacity-50 pointer-events-none',
            className
          )}
        >
          {/* Decrement / Remove Button */}
          <button
            type="button"
            onClick={handleDecrement}
            disabled={disabled || isAtMin}
            className={cn(
              'flex items-center justify-center',
              'hover:bg-[var(--primary-muted)]',
              'active:bg-[var(--primary-light)] active:opacity-80',
              'disabled:opacity-30 disabled:cursor-not-allowed',
              currentSize.button
            )}
            aria-label={shouldShowRemove ? 'Удалить' : 'Уменьшить'}
          >
            {shouldShowRemove ? (
              <X className={cn(currentSize.icon, 'text-[var(--text-tertiary)]')} weight="bold" />
            ) : (
              <MinusIcon className={cn(currentSize.icon, 'text-[var(--primary)]')} weight="bold" />
            )}
          </button>

          {/* Value Display */}
          <span
            className={cn(
              'font-semibold text-center text-[var(--primary)]',
              currentSize.text
            )}
          >
            {value}
          </span>

          {/* Increment Button */}
          <button
            type="button"
            onClick={handleIncrement}
            disabled={disabled || value >= max}
            className={cn(
              'flex items-center justify-center',
              'hover:bg-[var(--primary-muted)]',
              'active:bg-[var(--primary-light)] active:opacity-80',
              'disabled:opacity-30 disabled:cursor-not-allowed',
              currentSize.button
            )}
            aria-label="Увеличить"
          >
            <PlusIcon className={cn(currentSize.icon, 'text-[var(--primary)]')} weight="bold" />
          </button>
        </div>
      );
    }
  )
);
QuantitySelector.displayName = 'QuantitySelector';

export { QuantitySelector };
